import { DestroyRef, Directive, EffectRef, effect, inject, Injector, input, runInInjectionContext, untracked } from "@angular/core";
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

@Directive({
    selector: '[unique],[uniqueMatches]',
    providers: [{ provide: NG_VALIDATORS, useExisting: UniqueValidatorDirective, multi: true }],
    standalone: true
})

export class UniqueValidatorDirective implements Validator {
    readonly ignoreWhitespace = input<boolean>(false);
    readonly unique = input<boolean>(true);
    readonly uniqueMatches = input<any[]>([]);

    private whiteSpaceRegEx: RegExp = /(^\s*)|(\s*$)|(\s+(?=\s))/g;

    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private validatorDepsEffect?: EffectRef;

    constructor() {
        this.destroyRef.onDestroy(() => this.validatorDepsEffect?.destroy());
    }

    registerOnValidatorChange(fn: () => void): void {
        this.validatorDepsEffect?.destroy();
        runInInjectionContext(this.injector, () => {
            this.validatorDepsEffect = effect(() => {
                this.ignoreWhitespace();
                this.unique();
                this.uniqueMatches();
                untracked(() => fn());
            });
        });
    }

    validate(control: AbstractControl): ValidationErrors | null {
        let match: boolean;

        const uniqueMatches = this.uniqueMatches();
        if (!control.value || !uniqueMatches || !this.unique()) {
            return null;
        }

        if (typeof (control.value) === "string") {
            match = this.compareStrings(control);
        } else {
            match = uniqueMatches.some(um => um === control.value);
        }

        const validationVal = match
            ? { "unique": `This value must be unique` }
            : null;

        return validationVal;
    }

    compareStrings(control: AbstractControl): boolean {
        const controlValue: string = this.ignoreWhitespace()
            ? control.value.replace(this.whiteSpaceRegEx, "").toLowerCase()
            : control.value.toLowerCase();
        const matchValues: string[] = this.ignoreWhitespace()
            ? this.uniqueMatches().map(m => m.replace(this.whiteSpaceRegEx, "").toLowerCase())
            : this.uniqueMatches().map(m => m.toLowerCase());

        const hasMatch = matchValues.includes(controlValue);
        return hasMatch;
    }
}
